import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
    ContainedButton,
    Text,
    StyledCard,
    StyledCardContent,
    SignInForm,
    Header,
} from '../../components/index';

import ReCAPTCHA from '../../components/ReCAPTCHA';

import { requestOTP, forgotPassword } from '../../services';
import { alertError } from '../../redux/stateActions';

import { validPhone } from '../../utils/regex';

const STATE = Object.freeze({
    PASSWORD: 'PASSWORD',
    REQUEST: 'REQUEST',
    OTP: 'OTP',
    CONFIRM_PASSWORD: 'CONFIRM_PASSWORD',
    TERM: 'TERM',
    SUCCESS: 'SUCCESS',
});

const Forgot = ({ location, history }) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [state, setState] = useState(STATE.REQUEST);
    const [inputValue, setInputValue] = useState({
        phone: '',
        password: '',
        confirmPassword: '',
        otp: '',
    });
    const [OTPRef, setOTPRef] = useState('ABCB');
    const [countdown, setCountdown] = useState(0);
    const [token, setToken] = useState(null);
    const [agree, setAgree] = useState(false);
    const [user, setUser] = useState(null);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        const { search } = location;
        const queryParams = new URLSearchParams(search);
        const extractToken = queryParams.get('token');
        if (extractToken) {
            setToken(extractToken);
            setState(STATE.REQUEST);
        }
    }, []);

    const handleChange = (e) => {
        const newInputValue = { ...inputValue };
        const value = e.target.value;
        const name = e.target.name;

        if (name === 'term') {
            setAgree(e.target.checked);
        } else {
            newInputValue[name] = value;
        }
        setInputValue(newInputValue);
    };

    const handleRequestOTP = async () => {
        const newErrors = [];

        if (!validPhone.test(inputValue.phone)) {
            newErrors.push({
                property: 'phone',
                message: 'Please provide phone number.',
            });
        }

        if (newErrors.length) return setErrors(newErrors);

        setLoading(true);

        try {
            const resOTP = await requestOTP(inputValue.phone);
            if (!resOTP) {
                throw new Error(resOTP);
            }
            setState(STATE.CONFIRM_PASSWORD);
            setOTPRef(resOTP.refCode);
            setUser(resOTP.users_id);
            setTimer();
        } catch (error) {
            dispatch(alertError(error.response?.data?.message));
        }
        setLoading(false);
    };

    const handleResendOTP = async () => {
        setLoading(true);

        try {
            const resOTP = await requestOTP(inputValue.phone);
            if (!resOTP) {
                throw new Error(resOTP);
            }
            setOTPRef(resOTP.refCode);
            setInputValue({ ...inputValue, otp: '' });
            setTimer();
        } catch (error) {
            dispatch(alertError(error.response?.data?.message));
        }
        setLoading(false);
    };
    const handleResetPassword = async () => {
        setLoading(true);

        try {
            const password = inputValue.password;
            const confirmPassword = inputValue.confirmPassword;

            const newErrors = [];

            if (!password) {
                newErrors.push({
                    property: 'password',
                    message: 'Please provide password.',
                });
            }

            if (password.length < 6) {
                newErrors.push({
                    property: 'password',
                    message: 'Password must be at least 6 characters long.',
                });
            }

            if (!confirmPassword) {
                newErrors.push({
                    property: 'password',
                    message: 'Please provide confirm password.',
                });
            }

            if (confirmPassword.length < 6) {
                newErrors.push({
                    property: 'confirmPassword',
                    message: 'Password must be at least 6 characters long.',
                });
            }

            if (password !== confirmPassword) {
                newErrors.push({
                    property: 'password',
                    message: 'Password and Confirm password must be eqaul.',
                });
                newErrors.push({
                    property: 'confirmPassword',
                    message: 'Password and Confirm password must be eqaul.',
                });
            }
            console.log(newErrors);
            console.log(newErrors.length);

            if (newErrors.length) return setErrors(newErrors);

            const res = await forgotPassword({
                OTPNumber: inputValue.otp,
                refCode: OTPRef,
                users_id: user.id,
                password: inputValue.password,
            });
            setLoading(false);

            if (!res) {
                throw new Error(res);
            }
            setState(STATE.SUCCESS);
        } catch (error) {
            dispatch(alertError(error.response?.data?.message));
        } finally {
            setLoading(false);
        }
    };

    const setTimer = () => {
        let count = 30;
        setCountdown(count);
    };

    useEffect(() => {
        if (countdown > 0) {
            setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        }
    }, [countdown]);

    const handleRedirect = () => {
        history.push('/signin');
    };

    const onPressEnterPhoneInput = () => {
        if (state === STATE.REQUEST) {
            handleRequestOTP();
        }
    };

    const onPressEnterConfirmPasswordInput = () => {
        if (state === STATE.CONFIRM_PASSWORD) {
            handleResetPassword();
        }
    };

    /*---------- reCAPTCHA -----------*/
    const captchaRef = useRef(null);
    const [isVerify, setIsVerify] = useState(false);

    const handleVerifySuccess = () => {
        setIsVerify(true);
    };
    const handleVerifyError = () => {
        setIsVerify(false);
    };
    const handleVerifyExpire = () => {
        setIsVerify(false);
    };

    return (
        <div
            style={{
                maxWidth: '400px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                rowGap: '40px',
                padding: '0 20px 25px',
            }}
        >
            <Header
                showGoBack1
                onClickGoBack={() => history.goBack()}
                childrenContainerStyle={{ textAlign: 'center' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
                <Text $fontSize="24px" $bold>
                    {state === STATE.REQUEST
                        ? 'Forgot password'
                        : state === STATE.CONFIRM_PASSWORD
                        ? 'Change your password'
                        : 'Password Changed'}
                </Text>
                <Text $fontSize="14px" $color="gray2">
                    {state === STATE.REQUEST
                        ? 'Don’t worry. Resetting your password is easily. Just enter your mobile number you’re using for your account below. We will send you a OTP to reset your password'
                        : state === STATE.CONFIRM_PASSWORD
                        ? 'Enter 4 digits OTP code we just sent you on your email or mobile number.'
                        : 'Your password has been changed successfully. Use your new password to sign in. '}
                </Text>

                {(state === STATE.REQUEST || state === STATE.CONFIRM_PASSWORD) && (
                    <StyledCard
                        style={{
                            padding: '25px',
                        }}
                    >
                        <StyledCardContent
                            style={{
                                display: 'grid',
                                gridTemplateRows: '1fr minmax(auto,80%)',
                                height: '100%',
                            }}
                        >
                            <SignInForm
                                data={inputValue}
                                onChange={handleChange}
                                onResendOTP={handleResendOTP}
                                OTPRef={OTPRef}
                                state={state}
                                countdown={countdown}
                                onPressEnterPhoneInput={onPressEnterPhoneInput}
                                onPressEnterConfirmPasswordInput={onPressEnterConfirmPasswordInput}
                                errors={errors}
                            />
                            <div style={{ margin: '10px 0' }}>
                                <ReCAPTCHA
                                    ref={captchaRef}
                                    handleSuccess={handleVerifySuccess}
                                    handleError={handleVerifyError}
                                    handleExpire={handleVerifyExpire}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-end',
                                    marginTop: '10px',
                                }}
                            >
                                {state === STATE.REQUEST && (
                                    <ContainedButton
                                        $noShadow
                                        $width="215px"
                                        onClick={handleRequestOTP}
                                        loading={loading}
                                        disabled={!isVerify}
                                    >
                                        Request OTP
                                    </ContainedButton>
                                )}
                                {state === STATE.CONFIRM_PASSWORD && (
                                    <ContainedButton
                                        $noShadow
                                        $width="215px"
                                        onClick={handleResetPassword}
                                        style={{ marginTop: '5px' }}
                                        loading={loading}
                                        disabled={!isVerify}
                                    >
                                        Reset
                                    </ContainedButton>
                                )}
                            </div>
                        </StyledCardContent>
                    </StyledCard>
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: state === STATE.REQUEST ? '100px' : '20px',
                    }}
                >
                    {state === STATE.REQUEST || state === STATE.CONFIRM_PASSWORD ? (
                        <Text
                            $color="gray1"
                            $fontSize="14px"
                            style={{ textDecoration: 'underline' }}
                            onClick={handleRedirect}
                        >
                            Back to Sign in
                        </Text>
                    ) : (
                        <ContainedButton $noShadow $width="215px" onClick={handleRedirect}>
                            Back to sign in
                        </ContainedButton>
                    )}
                </div>
            </div>
        </div>
    );
};

Forgot.propTypes = {
    location: PropTypes.object,
    history: PropTypes.object,
};

export default Forgot;
